import { getLogger } from './logger';

const logger = getLogger('UI');

/**
 * Add animation styles to document if not already present
 */
export function ensureAnimationStyles(): void {
  logger.debug('Ensuring animation styles are present');
  const styleId = 'sirmaai-widget-styles';

  if (!document.getElementById(styleId)) {
    logger.debug('Animation styles not found, creating style element');
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      /* Glass morphism fallback for browsers without backdrop-filter */
      @supports not (backdrop-filter: blur(10px)) {
        .sirmaai-widget {
          background: rgba(255, 255, 255, 0.95) !important;
          backdrop-filter: none !important;
          -webkit-backdrop-filter: none !important;
        }
      }

      /* Respect reduced motion preferences */
      @media (prefers-reduced-motion: reduce) {
        .sirmaai-widget * {
          animation-duration: 0.01ms !important;
          animation-iteration-count: 1 !important;
          transition-duration: 0.01ms !important;
        }

        /* Pause SVG animations for reduced motion */
        .sirmaai-widget svg * {
          animation-duration: 0.01ms !important;
          animation-iteration-count: 1 !important;
          transition-duration: 0.01ms !important;
        }

        /* Show static state indicators when animations are disabled */
        .sirmaai-widget .state-visualization {
          opacity: 0.8 !important;
        }
      }

      /* High contrast mode support */
      @media (prefers-contrast: high) {
        .sirmaai-widget {
          background: rgba(255, 255, 255, 0.95) !important;
          border: 2px solid #000000 !important;
          color: #000000 !important;
        }
        .sirmaai-widget .sirmaai-header {
          background: rgba(0, 0, 0, 0.05) !important;
          border-bottom-color: #000000 !important;
        }
      }

      /* Widget animations */
      @keyframes sirmaai-fadeIn {
        from {
          opacity: 0;
          transform: translateY(20px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }

      @keyframes sirmaai-fadeOut {
        from {
          opacity: 1;
          transform: translateY(0);
        }
        to {
          opacity: 0;
          transform: translateY(10px);
        }
      }

      @keyframes sirmaai-pulse-soft {
        0%, 100% { opacity: 0.6; }
        50% { opacity: 1; }
      }

      @keyframes sirmaai-glow-green {
        0%, 100% {
          box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.4);
        }
        50% {
          box-shadow: 0 0 0 8px rgba(76, 175, 80, 0);
        }
      }

      /* Widget base class */
      .sirmaai-widget {
        animation: sirmaai-fadeIn 300ms ease-out;
        box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15), 0 4px 12px rgba(0, 0, 0, 0.1) !important;
      }

      .sirmaai-widget.hiding {
        animation: sirmaai-fadeOut 300ms ease-in forwards;
      }

      /* Stop button styles with accessibility */
      .sirmaai-stop-button {
        padding: 6px 12px !important;
        font-size: 13px !important;
        font-weight: 500 !important;
        border: 1px solid #FF5252 !important;
        background-color: #ffffff !important;
        color: #FF5252 !important;
        border-radius: 6px !important;
        cursor: pointer !important;
        transition: all 200ms ease-in-out !important;
        flex-shrink: 0 !important;
        min-width: 44px !important;
        min-height: 32px !important;
        font-family: inherit !important;
        line-height: 1.3 !important;
        white-space: nowrap !important;
      }

      .sirmaai-stop-button:hover {
        background-color: #FF5252 !important;
        color: #ffffff !important;
        transform: scale(1.02);
      }

      .sirmaai-stop-button:focus {
        outline: 2px solid #2196F3 !important;
        outline-offset: 2px !important;
      }

      .sirmaai-stop-button:active {
        transform: scale(0.98);
      }

      /* Icon container */
      .sirmaai-icon {
        width: 16px;
        height: 16px;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      /* Ensure CSS protection against host interference */
      .sirmaai-widget,
      .sirmaai-widget *,
      .sirmaai-widget *::before,
      .sirmaai-widget *::after {
        box-sizing: border-box !important;
      }

      /* SVG-based Widget position classes */
      .sirmaai-widget.position-top-left {
        top: 20px !important;
        left: 20px !important;
        bottom: auto !important;
        right: auto !important;
      }

      .sirmaai-widget.position-top-center {
        top: 20px !important;
        left: 50% !important;
        bottom: auto !important;
        right: auto !important;
        transform: translateX(-50%) !important;
      }

      .sirmaai-widget.position-top-right {
        top: 20px !important;
        right: 20px !important;
        bottom: auto !important;
        left: auto !important;
      }

      .sirmaai-widget.position-center-left {
        top: 50% !important;
        left: 20px !important;
        bottom: auto !important;
        right: auto !important;
        transform: translateY(-50%) !important;
      }

      .sirmaai-widget.position-center {
        top: 50% !important;
        left: 50% !important;
        bottom: auto !important;
        right: auto !important;
        transform: translate(-50%, -50%) !important;
      }

      .sirmaai-widget.position-center-right {
        top: 50% !important;
        right: 20px !important;
        bottom: auto !important;
        left: auto !important;
        transform: translateY(-50%) !important;
      }

      .sirmaai-widget.position-bottom-left {
        bottom: 20px !important;
        left: 20px !important;
        top: auto !important;
        right: auto !important;
      }

      .sirmaai-widget.position-bottom-center {
        bottom: 20px !important;
        left: 50% !important;
        top: auto !important;
        right: auto !important;
        transform: translateX(-50%) !important;
      }

      .sirmaai-widget.position-bottom-right {
        bottom: 20px !important;
        right: 20px !important;
        top: auto !important;
        left: auto !important;
      }

      /* SVG-based Widget base styles - MADE LARGER */
      .sirmaai-widget {
        margin: 0 !important;
        padding: 0 !important;
        text-align: left !important;
        all: initial;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, system-ui, sans-serif !important;
        font-size: 18px !important;
        position: fixed !important;
        bottom: 20px !important;
        right: 20px !important;
        width: 900px !important;
        max-width: 95vw !important;
        display: block !important;
        overflow: hidden !important;
        z-index: 9999 !important;
        border-radius: 16px !important;
        contain: layout style paint !important;
        color: #333333 !important;
        transform: translateZ(0);
        will-change: transform;
        aspect-ratio: 1462 / 751 !important;
      }

      /* Responsive design for different screen sizes - UPDATED */
      @media (max-width: 768px) {
        .sirmaai-widget {
          width: 95vw !important;
        }
      }

      /* High DPI displays */
      @media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi) {
        .sirmaai-widget svg {
          image-rendering: -webkit-optimize-contrast;
          image-rendering: crisp-edges;
        }
      }

      /* Cross-browser compatibility */
      @supports not (aspect-ratio: 1462 / 751) {
        .sirmaai-widget {
          height: auto !important;
        }
      }

      /* IE 11 compatibility */
      @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
        .sirmaai-widget {
          display: block !important;
          flex: none !important;
        }

        .sirmaai-widget svg {
          width: 100% !important;
          height: 100% !important;
          max-width: none !important;
        }
      }

      /* Safari specific fixes */
      @supports (-webkit-appearance: none) {
        .sirmaai-widget svg {
          -webkit-transform: translateZ(0);
          transform: translateZ(0);
        }
      }

      /* Firefox specific fixes */
      @supports (-moz-appearance: none) {
        .sirmaai-widget svg {
          shape-rendering: geometricPrecision;
        }
      }

      .sirmaai-widget svg {
        width: 100% !important;
        height: 100% !important;
        display: block !important;
        border-radius: 16px !important;
      }

      /* Optional disclaimer text: line one under the status text, line two in the footer */
      .sirmaai-widget .sirmaai-disclaimer {
        pointer-events: none !important;
        user-select: none !important;
      }

      /* The error message wraps into the area line one occupies, so hide it there */
      .sirmaai-widget[data-sirmaai-state="error"] #disclaimer-line-one {
        opacity: 0 !important;
        visibility: hidden !important;
      }

      .sirmaai-widget #stop-button {
        cursor: pointer !important;
        transition: all 200ms ease-in-out !important;
        filter: drop-shadow(0 1px 3px rgba(0, 0, 0, 0.12)) !important;
        transform-box: fill-box !important;
        transform-origin: center !important;
        /* Hide by default, show only in streaming state */
        opacity: 0 !important;
        visibility: hidden !important;
        pointer-events: none !important;
      }

      /* Show stop button only in streaming state */
      .sirmaai-widget[data-sirmaai-state="streaming"] #stop-button {
        opacity: 1 !important;
        visibility: visible !important;
        pointer-events: auto !important;
      }

      /* Make X icon paths non-interactive so clicks pass through to the circle */
      /* Target the two path elements that form the X on the stop button */
      .sirmaai-widget #stop-button ~ path[stroke="#00033C"] {
        pointer-events: none !important;
        /* Hide by default, show only in streaming state */
        opacity: 0 !important;
        visibility: hidden !important;
      }

      /* Show X icon only in streaming state */
      .sirmaai-widget[data-sirmaai-state="streaming"] #stop-button ~ path[stroke="#00033C"] {
        opacity: 1 !important;
        visibility: visible !important;
      }

      .sirmaai-widget #stop-button:hover {
        opacity: 0.85 !important;
        transform: scale(1.08) !important;
        filter: drop-shadow(0 2px 8px rgba(0, 0, 0, 0.15)) brightness(1.1) !important;
      }

      .sirmaai-widget #stop-button:focus {
        outline: 2px solid #2196F3 !important;
        outline-offset: 3px !important;
        transform: scale(1.02) !important;
      }

      .sirmaai-widget #stop-button:active {
        transform: scale(0.95) !important;
        filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.2)) brightness(0.95) !important;
        transition: all 80ms ease-in-out !important;
      }

      /* Enhanced visual feedback for better UX */
      .sirmaai-widget #stop-button[data-sirmaai-stop-button="true"] {
        will-change: transform, filter, opacity !important;
      }

      /* Add subtle pulse animation for drawing attention */
      @keyframes sirmai-stop-button-pulse {
        0% {
          transform: scale(1);
          filter: drop-shadow(0 1px 3px rgba(0, 0, 0, 0.12));
        }
        50% {
          transform: scale(1.03);
          filter: drop-shadow(0 2px 6px rgba(0, 0, 0, 0.18));
        }
        100% {
          transform: scale(1);
          filter: drop-shadow(0 1px 3px rgba(0, 0, 0, 0.12));
        }
      }

      .sirmaai-widget #stop-button.attention {
        animation: sirmai-stop-button-pulse 2s ease-in-out infinite !important;
      }

      .sirmaai-widget .sirmaai-content {
        display: flex !important;
        align-items: center !important;
        padding: 12px 16px !important;
        flex: 1 !important;
        gap: 8px !important;
        min-height: 44px !important;
        margin: 0 !important;
        border: none !important;
      }

      /* Audio Visualizer Styles */
      .sirmaai-widget .audio-visualizer {
        will-change: transform;
        transform: translateZ(0);
      }

      .sirmaai-widget .audio-bar {
        transition: fill 150ms ease-in-out;
        will-change: height, fill;
        transform: translateZ(0);
      }

      /* Static visualizer for reduced motion */
      @media (prefers-reduced-motion: reduce) {
        .sirmaai-widget .audio-bar {
          animation: none !important;
          transition: none !important;
        }
      }

      /* High contrast mode for audio visualizer */
      @media (prefers-contrast: high) {
        .sirmaai-widget .audio-bar {
          stroke: #000000 !important;
          stroke-width: 1px !important;
        }
      }

      /* State Visualization Styles - MAKE MORE PROMINENT */
      .sirmaai-widget #state-visualization {
        opacity: 1 !important;
        pointer-events: none !important;
        z-index: 10 !important;
      }

      /* Error state - circle outline */
      .sirmaai-widget #state-visualization circle[stroke="#FF5252"] {
        stroke: #FF5252 !important;
        filter: drop-shadow(0 2px 6px rgba(255, 82, 82, 0.3)) !important;
      }

      /* Ended state - success checkmark and circle */
      .sirmaai-widget #state-visualization circle[fill="#4CAF50"] {
        fill: #4CAF50 !important;
        filter: drop-shadow(0 2px 8px rgba(76, 175, 80, 0.4)) !important;
      }

      .sirmaai-widget #state-visualization path[stroke="#4CAF50"] {
        stroke: #4CAF50 !important;
        filter: drop-shadow(0 1px 4px rgba(76, 175, 80, 0.3)) !important;
      }
    `;

    logger.debug('Appending animation styles to document head');
    document.head.appendChild(style);
    logger.info('Animation styles added successfully');
  } else {
    logger.debug('Animation styles already present');
  }
}
